import { useState } from 'react';
import { usePersoneller, createPersonel, updatePersonel, deletePersonel, PersonelKayit, GOREV_SECENEKLERI } from './hooks/usePersoneller';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface AtananServis {
  id: string;
  servis_tarihi: string;
  servis_saati: string;
  islem_turu: string;
  servis_durumu: string;
  musteri_ad?: string;
  musteri_soyad?: string;
  musteri_adres?: string;
}

const SERVIS_DURUM_LABEL: Record<string, string> = {
  planlandi: 'Planlandı',
  yolda: 'Yolda',
  islemde: 'İşlemde',
  tamamlandi: 'Tamamlandı',
  iptal: 'İptal',
};

const DURUM_RENK: Record<string, string> = {
  planlandi: 'bg-sky-100 text-sky-700',
  yolda: 'bg-amber-100 text-amber-700',
  islemde: 'bg-orange-100 text-orange-700',
  tamamlandi: 'bg-emerald-100 text-emerald-700',
  iptal: 'bg-red-100 text-red-700',
};

export default function PersonelPage() {
  const { personeller, loading, error, refetch } = usePersoneller();
  const { profile } = useAuth();
  const [seciliPersonel, setSeciliPersonel] = useState<PersonelKayit | null>(null);
  const [servisler, setServisler] = useState<AtananServis[]>([]);
  const [servislerYukleniyor, setServislerYukleniyor] = useState(false);
  const [ekleModal, setEkleModal] = useState(false);
  const [duzenleModal, setDuzenleModal] = useState<PersonelKayit | null>(null);
  const [silConfirm, setSilConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [mesaj, setMesaj] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [form, setForm] = useState({ ad: '', soyad: '', telefon: '', email: '', gorev: 'Teknik Servis', diger_gorev: '', notlar: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const canEdit = profile?.rol === 'Yönetici' || profile?.rol === 'Ofis Personeli';

  const handleSecili = async (p: PersonelKayit) => {
    if (seciliPersonel?.id === p.id) { setSeciliPersonel(null); setServisler([]); return; }
    setSeciliPersonel(p);
    setServislerYukleniyor(true);
    const { data } = await supabase
      .from('sadem_servis_kayitlari')
      .select('*, sadem_musteriler(ad, soyad, acik_adres)')
      .eq('personel_id', p.id)
      .eq('aktif', true)
      .order('servis_tarihi', { ascending: false })
      .limit(20);
    if (data) {
      setServisler(data.map((s: any) => ({
        id: s.id, servis_tarihi: s.servis_tarihi, servis_saati: s.servis_saati,
        islem_turu: s.islem_turu, servis_durumu: s.servis_durumu,
        musteri_ad: s.sadem_musteriler?.ad, musteri_soyad: s.sadem_musteriler?.soyad,
        musteri_adres: s.sadem_musteriler?.acik_adres,
      })));
    }
    setServislerYukleniyor(false);
  };

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.ad.trim()) errs.ad = 'Ad zorunludur';
    if (!form.soyad.trim()) errs.soyad = 'Soyad zorunludur';
    if (!form.telefon.trim()) errs.telefon = 'Telefon zorunludur';
    if (!form.gorev) errs.gorev = 'Görev zorunludur';
    if (form.gorev === 'Diğer' && !form.diger_gorev.trim()) errs.diger_gorev = 'Görev açıklaması zorunludur';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleEkle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    setMesaj(null);
    try {
      await createPersonel(form);
      setMesaj({ type: 'success', text: 'Personel başarıyla eklendi.' });
      setEkleModal(false);
      resetForm();
      refetch();
      setTimeout(() => setMesaj(null), 3000);
    } catch (err: any) {
      setMesaj({ type: 'error', text: 'Personel kaydı oluşturulamadı. Lütfen bilgileri kontrol ediniz.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDuzenle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!duzenleModal || !validateForm()) return;
    setSaving(true);
    setMesaj(null);
    try {
      await updatePersonel(duzenleModal.id, { ...form, aktif: duzenleModal.aktif });
      setMesaj({ type: 'success', text: 'Personel bilgileri güncellendi.' });
      setDuzenleModal(null);
      resetForm();
      refetch();
      setTimeout(() => setMesaj(null), 3000);
    } catch (err: any) {
      setMesaj({ type: 'error', text: 'Personel güncellenemedi.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSil = async (id: string) => {
    try {
      await deletePersonel(id);
      setSilConfirm(null);
      if (seciliPersonel?.id === id) { setSeciliPersonel(null); setServisler([]); }
      refetch();
    } catch {
      setMesaj({ type: 'error', text: 'Bu işlem için yetkiniz bulunmuyor.' });
      setTimeout(() => setMesaj(null), 3000);
    }
  };

  const resetForm = () => setForm({ ad: '', soyad: '', telefon: '', email: '', gorev: 'Teknik Servis', diger_gorev: '', notlar: '' });

  const openDuzenle = (p: PersonelKayit) => {
    setDuzenleModal(p);
    const isCustom = !GOREV_SECENEKLERI.includes(p.gorev as any);
    setForm({
      ad: p.ad, soyad: p.soyad, telefon: p.telefon, email: p.email || '',
      gorev: isCustom ? 'Diğer' : p.gorev,
      diger_gorev: isCustom ? p.gorev : '',
      notlar: p.notlar || '',
    });
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-sky-400 bg-white';
  const errorCls = 'text-xs text-red-500 mt-0.5';

  if (loading) {
    return (
      <div className="text-center py-16">
        <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
        <p className="text-sm text-gray-500 mt-3">Personel verileri yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
        <p className="text-sm text-red-700 font-medium">Veriler yüklenemedi.</p>
        <button onClick={refetch} className="mt-3 text-sm text-red-600 font-semibold cursor-pointer">Tekrar Dene</button>
      </div>
    );
  }

  const aktifPersonel = personeller.filter(p => p.aktif);

  return (
    <div className="space-y-6">
      {mesaj && (
        <div className={`rounded-xl px-5 py-3 text-sm font-medium flex items-center gap-2 ${mesaj.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          <i className={mesaj.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
          {mesaj.text}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Personel Yönetimi</h2>
          <p className="text-sm text-gray-500 mt-0.5">{aktifPersonel.length} aktif personel</p>
        </div>
        <button onClick={() => { resetForm(); setEkleModal(true); }}
          className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-user-add-line"></i> Personel Ekle
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Personel', val: personeller.length, icon: 'ri-team-line', bg: 'bg-sky-100', color: 'text-sky-600' },
          { label: 'Aktif', val: aktifPersonel.length, icon: 'ri-user-follow-line', bg: 'bg-emerald-100', color: 'text-emerald-600' },
          { label: 'Bu Ay Servis', val: aktifPersonel.reduce((s, p) => s + (p.bu_ay_servis || 0), 0), icon: 'ri-tools-line', bg: 'bg-orange-100', color: 'text-orange-500' },
          { label: 'Toplam Servis', val: aktifPersonel.reduce((s, p) => s + (p.toplam_servis || 0), 0), icon: 'ri-check-double-line', bg: 'bg-violet-100', color: 'text-violet-600' },
        ].map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 font-medium">{k.label}</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{k.val}</p>
              </div>
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${k.bg}`}>
                <i className={`${k.icon} text-lg ${k.color}`}></i>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {personeller.map(p => {
            const isSelected = seciliPersonel?.id === p.id;
            return (
              <div key={p.id} onClick={() => handleSecili(p)}
                className={`bg-white rounded-xl border-2 p-5 cursor-pointer transition-all duration-200
                  ${isSelected ? 'border-sky-400 shadow-md' : 'border-gray-100 hover:border-sky-200'}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-sky-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {p.ad.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">{p.ad} {p.soyad}</p>
                      <p className="text-xs text-gray-400">{p.gorev}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${p.aktif ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${p.aktif ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                    {p.aktif ? 'Aktif' : 'Pasif'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    { label: 'Bu Ay', val: p.bu_ay_servis || 0 },
                    { label: 'Toplam', val: p.toplam_servis || 0 },
                    { label: 'Görev', val: p.gorev },
                  ].map(s => (
                    <div key={s.label} className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-base font-bold text-gray-800 truncate text-xs">{typeof s.val === 'string' ? s.val : s.val}</p>
                      <p className="text-xs text-gray-400">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <a href={`tel:${p.telefon}`} onClick={e => e.stopPropagation()}
                    className="flex-1 flex items-center justify-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-phone-line"></i> Ara
                  </a>
                  <a href={`https://wa.me/9${p.telefon.replace(/\s/g,'').replace(/^0/,'')}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                    className="flex-1 flex items-center justify-center gap-1 text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                    <i className="ri-whatsapp-line"></i> WhatsApp
                  </a>
                  {canEdit && (
                    <div className="flex gap-1">
                      <button onClick={e => { e.stopPropagation(); openDuzenle(p); }} className="w-8 h-8 flex items-center justify-center rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-600 cursor-pointer">
                        <i className="ri-pencil-line text-sm"></i>
                      </button>
                      <button onClick={e => { e.stopPropagation(); setSilConfirm(p.id); }} className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div>
          {seciliPersonel ? (
            <div className="bg-white rounded-xl border border-gray-100 sticky top-20">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-gray-800">{seciliPersonel.ad} {seciliPersonel.soyad}</p>
                  <p className="text-xs text-gray-400">Atanan Servisler</p>
                </div>
                <button onClick={() => { setSeciliPersonel(null); setServisler([]); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-400">
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
                {servislerYukleniyor ? (
                  <p className="text-sm text-gray-400 text-center py-8">Yükleniyor...</p>
                ) : servisler.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">Henüz atanmış servis bulunmuyor.</p>
                ) : servisler.map(s => (
                  <div key={s.id} className="px-5 py-3.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{s.musteri_ad} {s.musteri_soyad}</p>
                        <p className="text-xs text-gray-400 truncate">{s.musteri_adres}</p>
                        <p className="text-xs text-sky-600 font-medium mt-1">
                          {s.servis_tarihi ? new Date(s.servis_tarihi).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : '-'} — {s.servis_saati}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${DURUM_RENK[s.servis_durumu] || 'bg-gray-100 text-gray-600'}`}>
                          {SERVIS_DURUM_LABEL[s.servis_durumu] || s.servis_durumu}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{s.islem_turu}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <i className="ri-user-3-line text-slate-400 text-xl"></i>
              </div>
              <p className="text-sm text-gray-500 font-medium">Personel seçin</p>
              <p className="text-xs text-gray-400 mt-1">Bir personel kartına tıklayarak atanan servislerini görün</p>
            </div>
          )}
        </div>
      </div>

      {/* Ekleme Modal */}
      {ekleModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">Yeni Personel Ekle</h3>
              <button onClick={() => setEkleModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleEkle} className="space-y-3">
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ad *</label>
                <input required className={inputCls} value={form.ad} onChange={e => setForm(p => ({...p, ad: e.target.value}))} placeholder="Ahmet" />
                {errors.ad && <p className={errorCls}>{errors.ad}</p>}
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Soyad *</label>
                <input required className={inputCls} value={form.soyad} onChange={e => setForm(p => ({...p, soyad: e.target.value}))} placeholder="Yılmaz" />
                {errors.soyad && <p className={errorCls}>{errors.soyad}</p>}
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Telefon *</label>
                <input required className={inputCls} value={form.telefon} onChange={e => setForm(p => ({...p, telefon: e.target.value}))} placeholder="0532 xxx xx xx" />
                {errors.telefon && <p className={errorCls}>{errors.telefon}</p>}
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">E-posta</label>
                <input type="email" className={inputCls} value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="personel@firma.com" />
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Görev *</label>
                <select required className={inputCls} value={form.gorev} onChange={e => setForm(p => ({...p, gorev: e.target.value}))}>
                  {GOREV_SECENEKLERI.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {errors.gorev && <p className={errorCls}>{errors.gorev}</p>}
              </div>
              {form.gorev === 'Diğer' && (
                <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Görev Açıklaması *</label>
                  <input required className={inputCls} value={form.diger_gorev} onChange={e => setForm(p => ({...p, diger_gorev: e.target.value}))} placeholder="Görevi açıklayın..." />
                  {errors.diger_gorev && <p className={errorCls}>{errors.diger_gorev}</p>}
                </div>
              )}
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Notlar</label>
                <textarea className={`${inputCls} resize-none`} rows={2} value={form.notlar} onChange={e => setForm(p => ({...p, notlar: e.target.value}))} maxLength={200} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEkleModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-sky-600 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap hover:bg-sky-700 disabled:opacity-50">
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Düzenleme Modal */}
      {duzenleModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">Personel Düzenle</h3>
              <button onClick={() => setDuzenleModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleDuzenle} className="space-y-3">
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Ad *</label>
                <input required className={inputCls} value={form.ad} onChange={e => setForm(p => ({...p, ad: e.target.value}))} />
                {errors.ad && <p className={errorCls}>{errors.ad}</p>}
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Soyad *</label>
                <input required className={inputCls} value={form.soyad} onChange={e => setForm(p => ({...p, soyad: e.target.value}))} />
                {errors.soyad && <p className={errorCls}>{errors.soyad}</p>}
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Telefon *</label>
                <input required className={inputCls} value={form.telefon} onChange={e => setForm(p => ({...p, telefon: e.target.value}))} />
                {errors.telefon && <p className={errorCls}>{errors.telefon}</p>}
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">E-posta</label>
                <input type="email" className={inputCls} value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} />
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Görev</label>
                <select className={inputCls} value={form.gorev} onChange={e => setForm(p => ({...p, gorev: e.target.value}))}>
                  {GOREV_SECENEKLERI.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              {form.gorev === 'Diğer' && (
                <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Görev Açıklaması</label>
                  <input className={inputCls} value={form.diger_gorev} onChange={e => setForm(p => ({...p, diger_gorev: e.target.value}))} />
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold text-gray-500">Aktif</label>
                <button type="button"
                  onClick={() => setDuzenleModal(p => p ? { ...p, aktif: !p.aktif } : null)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${duzenleModal.aktif ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${duzenleModal.aktif ? 'left-5' : 'left-0.5'}`}></span>
                </button>
                <span className="text-xs text-gray-400">{duzenleModal.aktif ? 'Aktif' : 'Pasif'}</span>
              </div>
              <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Notlar</label>
                <textarea className={`${inputCls} resize-none`} rows={2} value={form.notlar} onChange={e => setForm(p => ({...p, notlar: e.target.value}))} maxLength={200} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setDuzenleModal(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-sky-600 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap hover:bg-sky-700 disabled:opacity-50">
                  {saving ? 'Kaydediliyor...' : 'Güncelle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Silme Onay */}
      {silConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-gray-800 text-center">Personel Silinsin mi?</h3>
            <p className="text-sm text-gray-500 text-center mt-1">Personel pasife alınacaktır.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setSilConfirm(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">İptal</button>
              <button onClick={() => handleSil(silConfirm)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">Evet, Sil</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}